'use client';

import { useEffect, useRef } from 'react';
import { claimCoupons, getAvailableCoupons, getUserSpaces, listProductsWithOptions } from '@/app/apis';
import {
  getDefaultCharacter2VideoModelVersion,
  getDefaultImg2VideoModelVersion,
  HOME_PAGE,
  isEnterpriseDistribution,
  SITE,
  TASK_PROGRESS_STORAGE_KEY,
} from '@/app/constants';
import {
  useClaw,
  useCredits,
  useFetch,
  useSubscription,
  useTourCoordinator,
  useTrials,
  useUserInfo,
} from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import {
  buildTeamSpaces,
  createPopupLoginSuccessMessage,
  findTeamSpaceById,
  getImpactImRef,
  getMaxProductDiscount,
  getPaidTrialInfo,
  isPersonalSpaceId,
  POPUP_LOGIN_QUERY_KEY,
  POPUP_LOGIN_QUERY_VALUE,
  persistImpactImRefFromUrl,
  persistXhsClickIdFromUrl,
} from '@/app/utils';
import {
  getDeviceId,
  initStorageCleanup,
  processReferrer,
  setBingEnhancedConversion,
  trackBingSignUp,
  trackGASignUp,
  trackMetaCompleteRegistration,
  trackYahooSignUp,
} from '@/app/utils/client';
import { usePathname, useRouter } from '@/i18n/routing';
import { impactTrackImpact } from './apis';
import { reportTrack } from './update-track';

export const AppClientInit = () => {
  const hasTrackedImpactRef = useRef(false);
  const hasReportedSignUpRef = useRef(false);
  const initializedUserIdRef = useRef<string | null>(null);
  const claimedInitUserIdRef = useRef<string | null>(null);
  const store = useStoreApi();
  const { updateSubscription } = useSubscription();
  const { updateCredits } = useCredits();
  const { updateTrials } = useTrials();
  const { getMyClaws } = useClaw();
  const { fetchTrialExpiration, fetchAvailableNexus, fetchAgentInviteInfo } = useUserInfo();
  const pathname = usePathname();
  const router = useRouter();

  const {
    userInfo,
    teamSpaceInfo,
    isUserInfoInitialized,
    isSubscriptionInitialized,
    updateTeamInfo,
    updateMaxProductDiscount,
    updateHasPaidTrialProduct,
    updateIsGiftPlan,
    updateAvailableCoupon,
    updateModelVersion,
  } = useStore((state) => ({
    userInfo: state.userInfo,
    teamSpaceInfo: state.teamSpaceInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
    isSubscriptionInitialized: state.isSubscriptionInitialized,
    updateTeamInfo: state.updateTeamInfo,
    updateMaxProductDiscount: state.updateMaxProductDiscount,
    updateHasPaidTrialProduct: state.updateHasPaidTrialProduct,
    updateIsGiftPlan: state.updateIsGiftPlan,
    updateAvailableCoupon: state.updateAvailableCoupon,
    updateModelVersion: state.updateModelVersion,
  }));

  useEffect(() => {
    if (!isUserInfoInitialized) return;

    const defaultImg2VideoModelVersion = getDefaultImg2VideoModelVersion();
    if (store.getState().img2videoFormParams.modelVersion !== defaultImg2VideoModelVersion) {
      updateModelVersion('img2video', defaultImg2VideoModelVersion);
    }

    const defaultCharacter2VideoModelVersion = getDefaultCharacter2VideoModelVersion();
    if (store.getState().character2videoFormParams.modelVersion !== defaultCharacter2VideoModelVersion) {
      updateModelVersion('character2video', defaultCharacter2VideoModelVersion);
    }
  }, [isUserInfoInitialized, store, updateModelVersion]);

  // 协调 Tour 队列和开屏弹窗队列
  useTourCoordinator();

  // 一次商品列表请求同时供两处使用：营销文案的最高折扣、以及是否存在付费试用档。
  // 试用档信息本就在同一份响应里，单独再拉一次没有必要。
  const fetchProductListSummary = () => {
    listProductsWithOptions({
      query: { channels: SITE === 'global' ? ['stripe'] : ['alipay'] },
    })
      .then(({ data: productsData }) => {
        if (!productsData) return;
        const allProducts = [...(productsData.subscription_products ?? []), ...(productsData.pass_products ?? [])];
        // 年付综合折扣（年对月 + 券）与单品折扣取最大，与 buildProductMap 口径一致
        const maxDiscount = getMaxProductDiscount(allProducts);

        if (maxDiscount > 0) {
          updateMaxProductDiscount(maxDiscount);
        }

        // 与订阅页试用 tab 同源判定：有试用档才允许开屏弹窗引导用户去试用
        updateHasPaidTrialProduct(getPaidTrialInfo(productsData.subscription_products ?? []) !== null);
      })
      // 拉取失败按"没有试用档"处理并标记已知，避免依赖方一直等
      .catch(() => updateHasPaidTrialProduct(false));
  };

  const { run: fetchTeamSpaces } = useFetch(getUserSpaces, {
    manual: true,
    onSuccess: ({ data }) => {
      const currentSpaceId = store.getState().userInfo?.internal?.space_id;
      if (isPersonalSpaceId(currentSpaceId)) {
        updateTeamInfo(null);
        return;
      }

      const nextTeamSpaceInfo = findTeamSpaceById(buildTeamSpaces(data?.spaces), currentSpaceId);
      updateTeamInfo(nextTeamSpaceInfo);
    },
  });

  const { run: getCouponAvailable } = useFetch(getAvailableCoupons, {
    manual: true,
    onSuccess: ({ data }) => {
      if (data) {
        updateAvailableCoupon(data.coupons?.map((v) => v.name ?? '') ?? []);
      }
    },
  });

  const { run: runClaimCoupons } = useFetch(claimCoupons, {
    manual: true,
    suppressUnauthorizedLoginDialog: true,
    onError: () => {},
  });

  const { run: trackImpact } = useFetch(impactTrackImpact, {
    manual: true,
    suppressUnauthorizedLoginDialog: true,
    onError: () => {},
  });

  // 兼容 google 登陆，邮箱，手机登陆，向 Yahoo 上报注册事件
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const isFirstLogin = searchParams.get('is_first_login');
    const isPopupLoginWindow =
      searchParams.get(POPUP_LOGIN_QUERY_KEY) === POPUP_LOGIN_QUERY_VALUE && window.opener && !window.opener.closed;
    if (
      !hasReportedSignUpRef.current &&
      isUserInfoInitialized &&
      (userInfo?.is_first_login || isFirstLogin === 'true')
    ) {
      hasReportedSignUpRef.current = true;
      trackYahooSignUp();
      trackBingSignUp();
      trackGASignUp({
        email: userInfo?.email,
        phoneNumber: userInfo?.phone,
      });
      // Meta Pixel 注册事件
      if (SITE === 'global') {
        trackMetaCompleteRegistration();
        // 调用 reportTrack 上报注册时的 fbp 和 fbc 参数
        const utmParams = processReferrer();
        reportTrack({
          utmSource: utmParams.utm_source || '',
          utmMedium: utmParams.utm_medium || '',
          utmCampaign: utmParams.utm_campaign || '',
          utmTerm: utmParams.utm_term || '',
          utmContent: utmParams.utm_content || '',
        });
      }

      // 如果用户有邮箱/手机号，设置 Bing 增强型转化
      setBingEnhancedConversion(userInfo?.email ?? '', userInfo?.phone ?? '');

      if (isPopupLoginWindow) {
        return;
      }

      // 如果是来自 google 登录的首次登录，且目前不在 /home 页面和 /my-subscription 页面，则跳转到 /home
      if (isFirstLogin === 'true' && pathname !== HOME_PAGE && !pathname.startsWith('/subscription/')) {
        router.replace(HOME_PAGE);
      }
    }
  }, [isUserInfoInitialized, userInfo, pathname, router]);

  const userId = userInfo?.id;
  const currentSpaceId = userInfo?.internal?.space_id;
  const currentTeamSpaceId = teamSpaceInfo?.space_id;

  useEffect(() => {
    if (!isUserInfoInitialized) {
      return;
    }

    if (!isEnterpriseDistribution) {
      fetchProductListSummary();
    }

    if (!userInfo) {
      initializedUserIdRef.current = null;
      updateTeamInfo(null);
      return;
    }

    if (initializedUserIdRef.current !== userId) {
      initializedUserIdRef.current = userId ?? null;
      fetch(`/api/canary?deviceId=${getDeviceId()}&userId=${userId}`).catch();
      fetchTrialExpiration();
      fetchAvailableNexus({
        query: { rule_set: ['sub_one_time_offer', 'mission_watch_ad_credit', 'claw_pass_bundle_first_purchase'] },
      });
      fetchAgentInviteInfo();
      if (userInfo.subs_plan === 'ultimate_v2') {
        getCouponAvailable({ query: { coupon_names: ['ultimate_v2_off_peak_coupon'] } });
      }
      if (!store.getState().creditsInfo) {
        updateCredits();
      }
      if (!store.getState().subscription) {
        updateSubscription();
      }
      if (!isEnterpriseDistribution) {
        getMyClaws();
      }
      updateTrials();
    }

    if (isSubscriptionInitialized) {
      updateIsGiftPlan();
    }

    if (isPersonalSpaceId(currentSpaceId)) {
      updateTeamInfo(null);
      return;
    }

    if (currentTeamSpaceId === currentSpaceId) {
      return;
    }

    fetchTeamSpaces();
  }, [
    currentSpaceId,
    currentTeamSpaceId,
    fetchAgentInviteInfo,
    fetchAvailableNexus,
    fetchTeamSpaces,
    fetchTrialExpiration,
    getCouponAvailable,
    getMyClaws,
    isSubscriptionInitialized,
    isUserInfoInitialized,
    store,
    updateCredits,
    updateIsGiftPlan,
    updateSubscription,
    updateTeamInfo,
    updateTrials,
    userId,
    userInfo,
  ]);

  useEffect(() => {
    if (!isUserInfoInitialized || !userId) {
      return;
    }

    const isInit = claimedInitUserIdRef.current !== userId;
    const isHomeOrSubscription = pathname.startsWith(HOME_PAGE) || pathname.startsWith('/subscription');
    if (!isInit && !isHomeOrSubscription) {
      return;
    }

    claimedInitUserIdRef.current = userId;
    runClaimCoupons({ body: {} });
  }, [isUserInfoInitialized, userId, pathname, runClaimCoupons]);

  useEffect(() => {
    if (!isUserInfoInitialized || !userInfo) {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get(POPUP_LOGIN_QUERY_KEY) !== POPUP_LOGIN_QUERY_VALUE) {
      return;
    }

    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(createPopupLoginSuccessMessage(), window.location.origin);
      window.close();
      return;
    }

    // 弹窗被浏览器拦截后 fallback 到当前页面的场景：
    // 清理 URL 中的 popup_login 参数，让后续首次登录重定向等逻辑正常执行
    searchParams.delete(POPUP_LOGIN_QUERY_KEY);
    const cleanSearch = searchParams.toString();
    const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`;
    window.history.replaceState(null, '', cleanUrl);
  }, [isUserInfoInitialized, userInfo]);

  useEffect(() => {
    persistXhsClickIdFromUrl();

    if (SITE !== 'global') {
      return;
    }

    persistImpactImRefFromUrl();
  }, [pathname]);

  useEffect(() => {
    if (SITE !== 'global' || hasTrackedImpactRef.current || !isUserInfoInitialized || !userInfo) {
      return;
    }

    const imRef = getImpactImRef();
    if (!imRef) {
      return;
    }

    hasTrackedImpactRef.current = true;
    trackImpact({
      body: {
        im_ref: imRef,
      },
    });
  }, [isUserInfoInitialized, trackImpact, userInfo]);

  useEffect(() => {
    // 清理过期的本地存储数据
    initStorageCleanup([
      TASK_PROGRESS_STORAGE_KEY,
      // TODO: 一段时间后，删除 VIDU_APP_DIALOG
      'VIDU_APP_DIALOG',
      'RELEASE_WEEK',
    ]);
  }, []);

  return null;
};
